import { createContext, useContext, useEffect, useMemo, useState } from "react";

/* ═════════════════════════════════════════════════════════════════════
   Locale context — powers the public site's language + currency selector.

   Currency conversion:
     • Base currency is INR (matches backend `monthly_price_inr` / `annual_price_inr`).
     • Rates are quoted as `1 INR = X <foreign>`, kept in one place below.
     • For a "wow" first pass we ship a static rate map. A later iteration can
       swap in a live FX feed (already integrated for statements) without
       touching consumers.

   Translation:
     • Minimal dictionary for the public nav CTAs + hero labels. Untranslated
       keys fall back to English so partial coverage never breaks the UI.
   ═════════════════════════════════════════════════════════════════════ */

// 1 INR = X FOREIGN — Feb 2026 approximate mid-market. Live rates below
// override these once /api/forex/rates responds successfully.
export const FX_RATES = {
    INR: 1,
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0094,
    AED: 0.044,
    SGD: 0.016,
};

async function fetchLiveRates(setter) {
    // Live FX from a public endpoint. INR base. Graceful fallback to static.
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const resp = await fetch(`${base}/api/forex/public/fx-rates`);
        if (!resp.ok) return;
        const data = await resp.json();
        const rates = data?.rates || {};
        const merged = { INR: 1 };
        for (const [k, v] of Object.entries(rates)) {
            if (typeof v === "number" && v > 0) merged[k] = v;
        }
        if (Object.keys(merged).length > 1) setter(merged);
    } catch { /* silent — fallback to static rates */ }
}

export const CURRENCY_SYMBOL = {
    INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED ", SGD: "S$",
};

const DICT = {
    en: {
        "nav.pricing": "Pricing",
        "nav.solutions": "Solutions",
        "nav.security": "Security",
        "nav.faq": "FAQ",
        "nav.beta": "Beta programme",
        "nav.sign_in": "Sign in",
        "nav.start_free": "Start free trial",
        "hero.cta_primary": "Start free trial",
        "hero.cta_secondary": "See live demo",
        "hero.headline_1": "Your financial life. Organised.",
        "hero.headline_2": "One secure VAULT.",
        "hero.sub": "The Personal Financial Operating System. Money, obligations, wealth, documents, household and insights — one organised workspace for individuals, couples and families.",
        "hero.get_started": "Get Started",
        "hero.view_demo": "View Demo",
        "statement.line_1": "See what you own.",
        "statement.line_2": "Know what you owe.",
        "statement.line_3": "Never miss what matters.",
        "benefit.control": "Control Your Money",
        "benefit.control_body": "Cards, expenses, payments and subscriptions.",
        "benefit.wealth": "Understand Your Wealth",
        "benefit.wealth_body": "Assets, investments, liabilities and net worth.",
        "benefit.ahead": "Stay Ahead",
        "benefit.ahead_body": "Reminders, renewals, alerts and financial insights.",
        "security.headline": "Your financial data stays private.",
        "final.headline_1": "Your finances deserve",
        "final.headline_2": "one organised home.",
        "final.cta": "Create Your VAULT",
        "cookies.title": "Cookies & your privacy",
        "cookies.body": "We use cookies to keep VAULT working, remember your preferences and help us improve. You can accept all, reject non-essential, or manage them individually. No non-essential cookie runs until you say so.",
        "cookies.accept_all": "Accept all",
        "cookies.reject": "Reject non-essential",
        "cookies.manage": "Manage preferences",
        "cookies.save": "Save preferences",
        "pricing.monthly": "Monthly",
        "pricing.annual": "Annual",
        "pricing.custom": "Custom",
        "pricing.free": "Free",
        "pricing.per_month": "/ month",
        "pricing.per_year": "/ year",
        "pricing.billed_in_inr": "Billed in INR; local prices are indicative",
        "solutions.individuals.title":     "Complete personal financial control.",
        "solutions.individuals.subtitle":  "Cards, cash, income, subscriptions, warranties and net worth — all in one calm command centre.",
        "solutions.individuals.cta":       "Start your VAULT",
        "solutions.families.title":        "One financial life, shared calmly.",
        "solutions.families.subtitle":     "Bring the household together — shared visibility, shared budgets, one net-worth number that actually reflects the family.",
        "solutions.families.cta":          "Set up your Family VAULT",
        "solutions.business.title":        "Separate personal and business without the mess.",
        "solutions.business.subtitle":     "Vault understands owner-operators. Tag every expense, every card, every income stream — and export a clean CA-ready pack anytime.",
        "solutions.business.cta":          "Set up your Business VAULT",
        "solutions.advisors.title":        "One workbench for every client engagement.",
        "solutions.advisors.subtitle":     "CA-ready packs, engagement dashboards, and client-facing checklists — built for practising Chartered Accountants and boutique advisors.",
        "solutions.advisors.cta":          "Try VAULT for CAs",
        "footer.tagline_1":  "The Personal Financial Operating System.",
        "footer.tagline_2":  "Organise your entire financial life in one intelligent VAULT.",
        "footer.cta_signup": "Start Free Trial",
        "footer.cta_dashboard": "Open Dashboard",
        "footer.group.product":   "Product",
        "footer.group.solutions": "Solutions",
        "footer.group.trust":     "Trust",
        "footer.group.legal":     "Legal",
        "footer.group.company":   "Company",
        "publicpage.faq.eyebrow":     "FAQ",
        "publicpage.faq.title":       "Answers to the questions we hear most.",
        "publicpage.security.eyebrow": "SECURITY CENTRE",
        "publicpage.security.title":   "Your financial information deserves serious protection.",
        "publicpage.security.subtitle": "Only capabilities we actually run in every deployment — no marketing fluff.",
    },
    hi: {
        "nav.pricing": "मूल्य",
        "nav.solutions": "समाधान",
        "nav.security": "सुरक्षा",
        "nav.faq": "FAQ",
        "nav.beta": "बीटा प्रोग्राम",
        "nav.sign_in": "साइन इन",
        "nav.start_free": "फ्री ट्रायल शुरू करें",
        "hero.cta_primary": "फ्री ट्रायल शुरू करें",
        "hero.cta_secondary": "लाइव डेमो देखें",
        "hero.headline_1": "आपका पूरा वित्तीय जीवन।",
        "hero.headline_2": "एक सुरक्षित VAULT।",
        "hero.sub": "आपके कार्ड, नकद, आय, निवेश, दस्तावेज़ और नेट वर्थ के लिए एक शांत जगह। कुछ भी बिखरा नहीं। कुछ भी छूटा नहीं।",
        "hero.get_started": "शुरू करें",
        "hero.view_demo": "डेमो देखें",
        "statement.line_1": "देखें आपके पास क्या है।",
        "statement.line_2": "जानें आप पर क्या बकाया है।",
        "statement.line_3": "जो मायने रखे उसे कभी न चूकें।",
        "benefit.control": "अपने पैसे को नियंत्रित करें",
        "benefit.control_body": "कार्ड, खर्च, भुगतान और सब्सक्रिप्शन।",
        "benefit.wealth": "अपनी संपत्ति समझें",
        "benefit.wealth_body": "एसेट्स, निवेश, देनदारियाँ और नेट वर्थ।",
        "benefit.ahead": "आगे रहें",
        "benefit.ahead_body": "रिमाइंडर, रिन्यूअल, अलर्ट और वित्तीय अंतर्दृष्टि।",
        "security.headline": "आपका वित्तीय डेटा निजी रहता है।",
        "final.headline_1": "आपके फाइनेंस के लिए",
        "final.headline_2": "एक व्यवस्थित घर।",
        "final.cta": "अपना VAULT बनाएँ",
        "cookies.title": "कुकीज़ और आपकी गोपनीयता",
        "cookies.body": "हम VAULT को चालू रखने, आपकी प्राथमिकताएँ याद रखने और सुधार में मदद के लिए कुकीज़ का उपयोग करते हैं। जब तक आप कहें नहीं, कोई भी गैर-आवश्यक कुकी नहीं चलती।",
        "cookies.accept_all": "सभी स्वीकारें",
        "cookies.reject": "गैर-आवश्यक अस्वीकारें",
        "cookies.manage": "प्राथमिकताएँ प्रबंधित करें",
        "cookies.save": "प्राथमिकताएँ सहेजें",
        "pricing.monthly": "मासिक",
        "pricing.annual": "वार्षिक",
        "pricing.custom": "कस्टम",
        "pricing.free": "मुफ़्त",
        "pricing.per_month": "/ माह",
        "pricing.per_year": "/ वर्ष",
        "pricing.billed_in_inr": "बिलिंग INR में; स्थानीय कीमतें केवल सूचनार्थ",
        "solutions.individuals.title":     "पूर्ण व्यक्तिगत वित्तीय नियंत्रण।",
        "solutions.individuals.subtitle":  "कार्ड, नकद, आय, सब्सक्रिप्शन, वारंटी और नेट वर्थ — सभी एक शांत कमांड सेंटर में।",
        "solutions.individuals.cta":       "अपना VAULT शुरू करें",
        "solutions.families.title":        "एक वित्तीय जीवन, शांति से साझा।",
        "solutions.families.subtitle":     "परिवार को एक साथ लाएँ — साझा दृश्यता, साझा बजट, एक नेट-वर्थ जो पूरे परिवार को दर्शाता है।",
        "solutions.families.cta":          "अपना Family VAULT बनाएँ",
        "solutions.business.title":        "व्यक्तिगत और बिज़नेस — बिना गड़बड़ी के अलग।",
        "solutions.business.subtitle":     "VAULT ओनर-ऑपरेटर्स को समझता है। हर खर्च, हर कार्ड, हर आय स्रोत को टैग करें — और कभी भी CA-रेडी पैक निर्यात करें।",
        "solutions.business.cta":          "अपना Business VAULT बनाएँ",
        "solutions.advisors.title":        "हर क्लाइंट एंगेजमेंट के लिए एक वर्कबेंच।",
        "solutions.advisors.subtitle":     "CA-रेडी पैक, एंगेजमेंट डैशबोर्ड और क्लाइंट-फेसिंग चेकलिस्ट — प्रैक्टिसिंग CAs और बुटीक एडवाइज़र्स के लिए।",
        "solutions.advisors.cta":          "CAs के लिए VAULT आज़माएँ",
        "footer.tagline_1":  "पर्सनल फाइनेंशियल ऑपरेटिंग सिस्टम।",
        "footer.tagline_2":  "अपने पूरे वित्तीय जीवन को एक बुद्धिमान VAULT में व्यवस्थित करें।",
        "footer.cta_signup": "फ्री ट्रायल शुरू करें",
        "footer.cta_dashboard": "डैशबोर्ड खोलें",
        "footer.group.product":   "उत्पाद",
        "footer.group.solutions": "समाधान",
        "footer.group.trust":     "भरोसा",
        "footer.group.legal":     "कानूनी",
        "footer.group.company":   "कंपनी",
        "publicpage.faq.eyebrow":     "FAQ",
        "publicpage.faq.title":       "जो सवाल हमें सबसे ज़्यादा मिलते हैं, उनके जवाब।",
        "publicpage.security.eyebrow": "सुरक्षा केंद्र",
        "publicpage.security.title":   "आपकी वित्तीय जानकारी को गंभीर सुरक्षा चाहिए।",
        "publicpage.security.subtitle": "केवल वे क्षमताएँ जो हम हर तैनाती में वास्तव में चलाते हैं — कोई मार्केटिंग बकवास नहीं।",
    },
    es: {
        "nav.pricing": "Precios",
        "nav.solutions": "Soluciones",
        "nav.security": "Seguridad",
        "nav.faq": "FAQ",
        "nav.beta": "Programa Beta",
        "nav.sign_in": "Iniciar sesión",
        "nav.start_free": "Prueba gratuita",
        "hero.cta_primary": "Prueba gratuita",
        "hero.cta_secondary": "Ver demo en vivo",
        "hero.headline_1": "Toda tu vida financiera.",
        "hero.headline_2": "Un VAULT seguro.",
        "hero.sub": "Un solo lugar tranquilo para tus tarjetas, efectivo, ingresos, inversiones, documentos y patrimonio neto. Nada disperso. Nada olvidado.",
        "hero.get_started": "Empezar",
        "hero.view_demo": "Ver demo",
        "statement.line_1": "Ve lo que posees.",
        "statement.line_2": "Sabe lo que debes.",
        "statement.line_3": "Nunca pierdas lo importante.",
        "benefit.control": "Controla tu dinero",
        "benefit.control_body": "Tarjetas, gastos, pagos y suscripciones.",
        "benefit.wealth": "Comprende tu patrimonio",
        "benefit.wealth_body": "Activos, inversiones, pasivos y patrimonio neto.",
        "benefit.ahead": "Adelántate",
        "benefit.ahead_body": "Recordatorios, renovaciones, alertas e insights financieros.",
        "security.headline": "Tus datos financieros son privados.",
        "final.headline_1": "Tus finanzas merecen",
        "final.headline_2": "un hogar ordenado.",
        "final.cta": "Crea tu VAULT",
        "cookies.title": "Cookies y tu privacidad",
        "cookies.body": "Usamos cookies para que VAULT funcione, recordar tus preferencias y mejorar. Puedes aceptar todas, rechazar las no esenciales o gestionarlas individualmente.",
        "cookies.accept_all": "Aceptar todo",
        "cookies.reject": "Rechazar no esenciales",
        "cookies.manage": "Gestionar preferencias",
        "cookies.save": "Guardar preferencias",
        "pricing.monthly": "Mensual",
        "pricing.annual": "Anual",
        "pricing.custom": "Personalizado",
        "pricing.free": "Gratis",
        "pricing.per_month": "/ mes",
        "pricing.per_year": "/ año",
        "pricing.billed_in_inr": "Facturado en INR; los precios locales son indicativos",
        "solutions.individuals.title":     "Control financiero personal completo.",
        "solutions.individuals.subtitle":  "Tarjetas, efectivo, ingresos, suscripciones, garantías y patrimonio neto — todo en un centro de mando tranquilo.",
        "solutions.individuals.cta":       "Comienza tu VAULT",
        "solutions.families.title":        "Una vida financiera, compartida en calma.",
        "solutions.families.subtitle":     "Reúne al hogar — visibilidad compartida, presupuestos compartidos, un patrimonio neto que refleja realmente a la familia.",
        "solutions.families.cta":          "Configura tu Family VAULT",
        "solutions.business.title":        "Separa personal y negocio sin desorden.",
        "solutions.business.subtitle":     "VAULT entiende a los owner-operators. Etiqueta cada gasto, cada tarjeta, cada ingreso — y exporta un pack listo para tu contador cuando quieras.",
        "solutions.business.cta":          "Configura tu Business VAULT",
        "solutions.advisors.title":        "Una mesa de trabajo para cada cliente.",
        "solutions.advisors.subtitle":     "Packs listos para CA, paneles de engagement y listas para clientes — pensado para CAs en ejercicio y asesores boutique.",
        "solutions.advisors.cta":          "Prueba VAULT para CAs",
        "footer.tagline_1":  "El Sistema Operativo Financiero Personal.",
        "footer.tagline_2":  "Organiza toda tu vida financiera en un VAULT inteligente.",
        "footer.cta_signup": "Prueba gratuita",
        "footer.cta_dashboard": "Abrir panel",
        "footer.group.product":   "Producto",
        "footer.group.solutions": "Soluciones",
        "footer.group.trust":     "Confianza",
        "footer.group.legal":     "Legal",
        "footer.group.company":   "Compañía",
        "publicpage.faq.eyebrow":     "FAQ",
        "publicpage.faq.title":       "Respuestas a las preguntas más frecuentes.",
        "publicpage.security.eyebrow": "CENTRO DE SEGURIDAD",
        "publicpage.security.title":   "Tu información financiera merece protección real.",
        "publicpage.security.subtitle": "Solo capacidades que ejecutamos en cada despliegue — sin marketing vacío.",
    },
    fr: {
        "nav.pricing": "Tarifs",
        "nav.solutions": "Solutions",
        "nav.security": "Sécurité",
        "nav.faq": "FAQ",
        "nav.beta": "Programme Bêta",
        "nav.sign_in": "Se connecter",
        "nav.start_free": "Essai gratuit",
        "hero.cta_primary": "Essai gratuit",
        "hero.cta_secondary": "Voir la démo",
        "hero.headline_1": "Toute votre vie financière.",
        "hero.headline_2": "Un VAULT sécurisé.",
        "hero.sub": "Un seul endroit apaisé pour vos cartes, espèces, revenus, placements, documents et patrimoine net. Rien d'éparpillé. Rien d'oublié.",
        "hero.get_started": "Commencer",
        "hero.view_demo": "Voir la démo",
        "statement.line_1": "Voyez ce que vous possédez.",
        "statement.line_2": "Sachez ce que vous devez.",
        "statement.line_3": "Ne manquez jamais l'essentiel.",
        "benefit.control": "Contrôlez votre argent",
        "benefit.control_body": "Cartes, dépenses, paiements et abonnements.",
        "benefit.wealth": "Comprenez votre patrimoine",
        "benefit.wealth_body": "Actifs, placements, dettes et patrimoine net.",
        "benefit.ahead": "Gardez une longueur d'avance",
        "benefit.ahead_body": "Rappels, renouvellements, alertes et insights financiers.",
        "security.headline": "Vos données financières restent privées.",
        "final.headline_1": "Vos finances méritent",
        "final.headline_2": "un foyer bien rangé.",
        "final.cta": "Créez votre VAULT",
        "cookies.title": "Cookies et votre vie privée",
        "cookies.body": "Nous utilisons des cookies pour faire fonctionner VAULT, mémoriser vos préférences et nous améliorer. Vous pouvez tout accepter, refuser les non essentiels ou les gérer.",
        "cookies.accept_all": "Tout accepter",
        "cookies.reject": "Refuser les non essentiels",
        "cookies.manage": "Gérer les préférences",
        "cookies.save": "Enregistrer",
        "pricing.monthly": "Mensuel",
        "pricing.annual": "Annuel",
        "pricing.custom": "Sur devis",
        "pricing.free": "Gratuit",
        "pricing.per_month": "/ mois",
        "pricing.per_year": "/ an",
        "pricing.billed_in_inr": "Facturé en INR ; prix locaux indicatifs",
        "solutions.individuals.title":     "Contrôle financier personnel complet.",
        "solutions.individuals.subtitle":  "Cartes, espèces, revenus, abonnements, garanties et patrimoine net — tout dans un centre de contrôle apaisé.",
        "solutions.individuals.cta":       "Commencez votre VAULT",
        "solutions.families.title":        "Une vie financière, partagée sereinement.",
        "solutions.families.subtitle":     "Rassemblez le foyer — visibilité partagée, budgets partagés, un patrimoine net qui reflète vraiment la famille.",
        "solutions.families.cta":          "Configurez votre Family VAULT",
        "solutions.business.title":        "Séparez perso et pro sans le désordre.",
        "solutions.business.subtitle":     "VAULT comprend les owner-operators. Taggez chaque dépense, chaque carte, chaque flux de revenus — et exportez un pack prêt pour votre comptable.",
        "solutions.business.cta":          "Configurez votre Business VAULT",
        "solutions.advisors.title":        "Un poste de travail pour chaque client.",
        "solutions.advisors.subtitle":     "Packs prêts pour CA, tableaux d'engagement et check-lists client — pensé pour CAs en exercice et cabinets boutique.",
        "solutions.advisors.cta":          "Essayez VAULT pour CAs",
        "footer.tagline_1":  "Le Système d'Exploitation Financier Personnel.",
        "footer.tagline_2":  "Organisez toute votre vie financière dans un VAULT intelligent.",
        "footer.cta_signup": "Essai gratuit",
        "footer.cta_dashboard": "Ouvrir le tableau de bord",
        "footer.group.product":   "Produit",
        "footer.group.solutions": "Solutions",
        "footer.group.trust":     "Confiance",
        "footer.group.legal":     "Légal",
        "footer.group.company":   "Entreprise",
        "publicpage.faq.eyebrow":     "FAQ",
        "publicpage.faq.title":       "Réponses aux questions les plus fréquentes.",
        "publicpage.security.eyebrow": "CENTRE DE SÉCURITÉ",
        "publicpage.security.title":   "Vos données financières méritent une vraie protection.",
        "publicpage.security.subtitle": "Uniquement des capacités que nous exécutons réellement — pas de marketing creux.",
    },
};

const KEY = "vault.locale";
const LocaleCtx = createContext(null);

export function LocaleProvider({ children }) {
    const [locale, setLocale] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(KEY) || "{}");
            // Deep-link params override storage on first paint. `?lang=hi&ccy=USD`
            // preserves the sender's locale when a global marketing email links you here.
            if (typeof window !== "undefined") {
                const sp = new URLSearchParams(window.location.search);
                const langParam = (sp.get("lang") || "").toLowerCase();
                const ccyParam = (sp.get("ccy") || sp.get("currency") || "").toUpperCase();
                if (langParam && ["en", "hi", "es", "fr"].includes(langParam)) stored.lang = langParam;
                if (ccyParam && ["INR", "USD", "EUR", "GBP", "AED", "SGD"].includes(ccyParam)) stored.currency = ccyParam;

                // Auto-detect currency from browser locale if nothing chosen yet.
                if (!stored.currency) {
                    const nav = (navigator.language || "en-US").toUpperCase();
                    const region = nav.split("-")[1] || "";
                    const REGION_TO_CCY = {
                        IN: "INR", US: "USD", GB: "GBP", AE: "AED", SG: "SGD",
                        DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR",
                        IE: "EUR", PT: "EUR", AT: "EUR", BE: "EUR", FI: "EUR",
                    };
                    if (REGION_TO_CCY[region]) stored.currency = REGION_TO_CCY[region];
                }
            }
            return stored;
        } catch { return {}; }
    });
    const [liveRates, setLiveRates] = useState(null);

    // Fetch live FX once at mount — cached daily server-side
    useEffect(() => { fetchLiveRates(setLiveRates); }, []);

    // Listen for events dispatched by the standalone selector so both stay in sync
    useEffect(() => {
        const onChange = (e) => setLocale(e.detail || {});
        window.addEventListener("vault:locale-change", onChange);
        return () => window.removeEventListener("vault:locale-change", onChange);
    }, []);

    // Persist
    useEffect(() => {
        try { localStorage.setItem(KEY, JSON.stringify(locale)); } catch { /* noop */ }
    }, [locale]);

    const value = useMemo(() => {
        const lang = locale.lang || "en";
        const currency = locale.currency || "INR";
        const dict = DICT[lang] || DICT.en;
        const rateSource = liveRates || FX_RATES;
        const rate = rateSource[currency] ?? FX_RATES[currency] ?? 1;
        const symbol = CURRENCY_SYMBOL[currency] || `${currency} `;
        const rateSourceLabel = liveRates ? "live" : "static";

        const t = (key) => (dict[key] ?? DICT.en[key] ?? key);

        const fmtPrice = (amountInr, opts = {}) => {
            if (amountInr == null) return null;
            if (amountInr === 0) return t("pricing.free");
            const converted = amountInr * rate;
            const digits = opts.decimals ?? (currency === "INR" ? 0 : 2);
            const asStr = converted.toLocaleString(undefined, {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            });
            return `${symbol}${asStr}`;
        };

        const updateLocale = (patch) => setLocale((prev) => ({ ...prev, ...patch }));

        return { lang, currency, symbol, rate, t, fmtPrice, updateLocale, rateSource: rateSourceLabel };
    }, [locale, liveRates]);

    return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale() {
    const ctx = useContext(LocaleCtx);
    if (!ctx) {
        // Safe fallback so consumers outside the provider (e.g. authed shell) still render
        return {
            lang: "en",
            currency: "INR",
            symbol: "₹",
            rate: 1,
            t: (k) => (DICT.en[k] ?? k),
            fmtPrice: (n) => (n == null ? null : n === 0 ? "Free" : `₹${Number(n).toLocaleString("en-IN")}`),
            updateLocale: () => {},
        };
    }
    return ctx;
}
